import { GlButton, GlButtonGroup, GlDisclosureDropdown, GlIcon, GlTooltipDirective } from '@gitlab/ui';
import { mapState } from 'pinia';
import { __, n__ } from '~/locale';
import { keysFor, MR_NEXT_UNRESOLVED_DISCUSSION, MR_PREVIOUS_UNRESOLVED_DISCUSSION } from '~/behaviors/shortcuts/keybindings';
import { keyboardShortcutsDisabled } from '~/behaviors/shortcuts/shortcuts_disabled';
import { sanitize } from '~/lib/dompurify';
import { useNotes } from '~/notes/store/legacy_notes';
import discussionNavigation from '../mixins/discussion_navigation';
export default {
    name: 'DiscussionCounter',
    i18n: {
        allThreadsResolved: __('All threads resolved!'),
        threadOptions: __('Thread options')
    },
    directives: {
        GlTooltip: GlTooltipDirective
    },
    components: {
        GlDisclosureDropdown,
        GlButton,
        GlButtonGroup,
        GlIcon
    },
    mixins: [
        discussionNavigation
    ],
    inject: {
        store: {
            type: Object
        }
    },
    props: {
        blocksMerge: {
            type: Boolean,
            required: true
        },
        canResolveDiscussion: {
            type: Boolean,
            required: true
        },
        compact: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    computed: {
        ...mapState(useNotes, [
            'getNoteableData',
            'resolvableDiscussionsCount',
            'unresolvedDiscussionsCount'
        ]),
        allResolved () {
            return this.unresolvedDiscussionsCount === 0;
        },
        toggleThreadsLabel () {
            return !this.store.allVisibleDiscussionsExpanded ? __('Show all comments') : __('Hide all comments');
        },
        nextUnresolvedDiscussionShortcutKey () {
            return keyboardShortcutsDisabled() ? null : keysFor(MR_NEXT_UNRESOLVED_DISCUSSION)[0];
        },
        nextUnresolvedDiscussionTitle () {
            return MR_NEXT_UNRESOLVED_DISCUSSION.description;
        },
        nextUnresolvedDiscussionTooltip () {
            const description = this.nextUnresolvedDiscussionTitle;
            const key = this.nextUnresolvedDiscussionShortcutKey;
            return keyboardShortcutsDisabled() ? description : sanitize(`${description} <kbd class="flat gl-ml-1" aria-hidden=true>${key}</kbd>`);
        },
        previousUnresolvedDiscussionShortcutKey () {
            return keyboardShortcutsDisabled() ? null : keysFor(MR_PREVIOUS_UNRESOLVED_DISCUSSION)[0];
        },
        previousUnresolvedDiscussionTitle () {
            return MR_PREVIOUS_UNRESOLVED_DISCUSSION.description;
        },
        previousUnresolvedDiscussionTooltip () {
            const description = this.previousUnresolvedDiscussionTitle;
            const key = this.previousUnresolvedDiscussionShortcutKey;
            return keyboardShortcutsDisabled() ? description : sanitize(`${description} <kbd class="flat gl-ml-1" aria-hidden=true>${key}</kbd>`);
        },
        compactTooltip () {
            if (this.allResolved) {
                return this.$options.i18n.allThreadsResolved;
            }
            return n__('%d open thread', '%d open threads', this.unresolvedDiscussionsCount);
        },
        compactCount () {
            return this.allResolved ? this.resolvableDiscussionsCount : this.unresolvedDiscussionsCount;
        },
        resolveAllDiscussionsIssuePath () {
            return this.getNoteableData.create_issue_to_resolve_discussions_path;
        },
        threadOptions () {
            const options = [
                {
                    text: this.toggleThreadsLabel,
                    action: ()=>this.store.toggleAllVisibleDiscussions(),
                    extraAttrs: {
                        'data-testid': 'toggle-all-discussions-btn'
                    }
                }
            ];
            if (this.canResolveDiscussion && this.resolveAllDiscussionsIssuePath && !this.allResolved) {
                options.push({
                    text: __('Resolve all with new issue'),
                    href: this.resolveAllDiscussionsIssuePath,
                    extraAttrs: {
                        'data-testid': 'resolve-all-with-issue-link'
                    }
                });
            }
            return options;
        }
    }
};
